// Klinik Genel Bakış — tek kliniğe scope'lu dashboard. Analiz dili
// (design-principles §4): başlık şeridi → slate50 zemin → KPI satırı → sabit
// gövdeli kartlar. Yumuşak tipografi: 800/900 kalınlık yok, değerler 700.
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Avatar, ButtonBase, Skeleton, alpha } from "@mui/material";
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  EventNote as EventNoteIcon,
  ReceiptLongOutlined as ActivityIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import EmptyState from "../../components/atoms/EmptyState";
import { useClinicTheme } from "../../theme";
import useAuth from "../../hooks/useAuth";
import useTerms from "../../hooks/useTerms";
import useDashboardSocket from "../../hooks/useDashboardSocket";
import PeriodSelector, { DASHBOARD_PERIODS } from "../../components/common/PeriodSelector";
import SectionCard from "../../components/molecules/SectionCard";
import PageHeaderBar from "../../components/molecules/PageHeaderBar";
import KpiTile from "../../components/molecules/KpiTile";
import {
  COLORS,
  ACCENTS,
  TYPO,
  CARD_PAD,
  CARD_GAP,
  CARD_PAGE_PAD,
  dashboardSectionSx,
} from "../../styles/uiConstants";
import { getRoleLabel } from "../../utils/roleLabels";
import {
  useLazyGetClinicStatsQuery,
  useLazyGetClinicMonthlyAppointmentsQuery,
  useLazyGetClinicActivityQuery,
  useLazyGetClinicStaffActivityQuery,
} from "../../store/dashboardApi";

/** Randevu grafiğinde gösterilen ay sayısı. */
const MONTHS_IN_CHART = 6;
/** Kart gövde yükseklikleri sabit — yan yana kartlar birbirini uzatmaz. */
const TOP_BODY_HEIGHT = 340;
const ACTIVITY_BODY_HEIGHT = 360;

const ACTIVITY_META = {
  treatment: { label: "Tedavi", color: ACCENTS.indigo },
  appointment: { label: "Randevu", color: ACCENTS.blue },
  device: { label: "Cihaz", color: ACCENTS.cyan },
  patient: { label: "Hasta", color: ACCENTS.emerald },
  prescription: { label: "Reçete", color: ACCENTS.violet },
};

const nf = (n) => Number(n || 0).toLocaleString("tr-TR");
const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);
const periodLabel = (period) => DASHBOARD_PERIODS.find((p) => p.value === period)?.label || "Bugün";

const scrollSx = {
  overflowY: "auto",
  overscrollBehavior: "contain",
  scrollbarWidth: "thin",
};

/** Kart: başlık + gövde (dar ekranda sabit yükseklik, genişte satırı doldurur; design-principles §4). */
function DashCard({ icon, title, subtitle, action, bodyHeight, flush = false, children, sx }) {
  return (
    <SectionCard
      icon={icon}
      title={title}
      subtitle={subtitle}
      action={action}
      sx={{ ...dashboardSectionSx, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0, ...sx }}
    >
      {/* Geniş ekranda gövde satırın kalan yüksekliğini doldurur (pano ekrana sığar);
          dar ekranda sabit yükseklik + sayfa kaydırma. */}
      <Box
        sx={{
          p: flush ? 0 : CARD_PAD,
          height: { xs: bodyHeight, lg: "auto" },
          flex: { lg: 1 },
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {children}
      </Box>
    </SectionCard>
  );
}

function CardEmpty({ icon, title, description }) {
  return (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <EmptyState variant="illustrated-sm" icon={icon} title={title} description={description} sx={{ py: 0 }} />
    </Box>
  );
}

/** Önceki döneme göre değişim — KPI açıklamasında renkli, okla. */
function TrendHint({ change, direction, goodWhenUp = true, suffix = "önceki döneme göre" }) {
  if (change === undefined || change === null || !direction || direction === "neutral") {
    return <>{suffix ? `Değişim yok · ${suffix}` : "Değişim yok"}</>;
  }
  const good = goodWhenUp ? direction === "up" : direction === "down";
  const Icon = direction === "up" ? TrendingUpIcon : TrendingDownIcon;
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
      <Box
        component="span"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontWeight: 600, color: good ? COLORS.successDark : COLORS.errorDark }}
      >
        <Icon sx={{ fontSize: 14 }} />
        {direction === "up" ? "+" : "−"}
        {nf(Math.abs(change))}
      </Box>
      {suffix}
    </Box>
  );
}

/* ── Aylık Randevu Grafiği ──────────────────────────────────────────── */
function ChartTip({ active, payload, label, series }) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        bgcolor: COLORS.white,
        border: `1px solid ${COLORS.slate200}`,
        borderRadius: 2,
        boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
        px: 1.5,
        py: 1,
        minWidth: 140,
      }}
    >
      <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mb: 0.5 }}>{label}</Typography>
      {series.map((s) => {
        const p = payload.find((x) => x.dataKey === s.key);
        return (
          <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 10, height: 2, borderRadius: 1, bgcolor: s.color }} />
            <Typography sx={{ fontSize: TYPO.body, fontWeight: 700, color: COLORS.slate800 }}>{nf(p?.value)}</Typography>
            <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>{s.label}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** Lejant: renk + etiket + sayı + yüzde (kimlik yalnız renge bırakılmaz). */
function ChartLegend({ series, totals }) {
  const total = series.reduce((sum, s) => sum + (totals[s.key] || 0), 0);
  return (
    <Box sx={{ display: "flex", gap: 2.5, flexWrap: "wrap", mb: 1.5 }}>
      {series.map((s) => (
        <Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: s.color }} />
          <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate600 }}>
            {s.label}
            <Box component="span" sx={{ fontWeight: 600, color: COLORS.slate800, ml: 0.75 }}>
              {nf(totals[s.key])}
            </Box>
            <Box component="span" sx={{ color: COLORS.slate400, ml: 0.5 }}>
              %{pct(totals[s.key], total)}
            </Box>
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

const SK_BARS = [55, 40, 70, 50, 85, 65];
function BarsSkeleton() {
  return (
    <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 3, px: 2, pb: 3 }}>
      {SK_BARS.map((h, i) => (
        <Box key={i} sx={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 0.5, height: "100%" }}>
          <Skeleton variant="rounded" sx={{ flex: 1, height: `${h}%`, borderRadius: "4px 4px 0 0" }} />
          <Skeleton variant="rounded" sx={{ flex: 1, height: `${h / 4}%`, borderRadius: "4px 4px 0 0" }} />
        </Box>
      ))}
    </Box>
  );
}

function RowsSkeleton({ rows = 6, avatar = false }) {
  return (
    <Box>
      {[...Array(rows)].map((_, i) => (
        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, px: CARD_PAD, py: 1.25, borderBottom: `1px solid ${COLORS.slate100}` }}>
          {avatar ? <Skeleton variant="circular" width={32} height={32} /> : <Skeleton variant="circular" width={8} height={8} />}
          <Box sx={{ flex: 1 }}>
            <Skeleton width={`${45 + ((i * 13) % 35)}%`} />
          </Box>
          <Skeleton width={48} />
        </Box>
      ))}
    </Box>
  );
}

/* ── Main Component ─────────────────────────────────────────────────── */
export default function ClinicDashboard() {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const primary = palette.primary;
  const navigate = useNavigate();
  const [period, setPeriod] = useState("today");

  const { auth } = useAuth();
  const token = auth?.accessToken;
  const clinicId = auth?.clinic?._id || auth?.user?.clinic?._id;
  const clinicName = auth?.clinic?.name || auth?.user?.clinic?.name || "Klinik";

  // Gerçek zamanlı güncelleme
  const { stats: liveStats } = useDashboardSocket(clinicId ? `clinic:${clinicId}` : null);

  const [fetchStats, { data: statsData, isLoading: statsLoading }] = useLazyGetClinicStatsQuery();
  const [fetchMonthly, { data: monthlyData, isLoading: monthlyLoading }] = useLazyGetClinicMonthlyAppointmentsQuery();
  const [fetchActivity, { data: activityData, isFetching: activityFetching }] = useLazyGetClinicActivityQuery();
  const [fetchStaffActivity, { data: staffData, isFetching: staffFetching }] = useLazyGetClinicStaffActivityQuery();

  useEffect(() => {
    if (!token) return;
    fetchStats({ token, period });
    fetchMonthly({ token, months: MONTHS_IN_CHART });
    fetchActivity({ token, period });
    fetchStaffActivity({ token, period });
  }, [token, period, fetchStats, fetchMonthly, fetchActivity, fetchStaffActivity]);

  const stats = liveStats || statsData?.data;
  const activity = useMemo(() => activityData?.data || [], [activityData]);
  const staffActivity = useMemo(() => staffData?.data || [], [staffData]);
  const monthlyAppointments = useMemo(() => monthlyData?.data || [], [monthlyData]);
  const monthlyTotals = useMemo(
    () =>
      monthlyAppointments.reduce(
        (acc, m) => ({ completed: acc.completed + (m.completed || 0), cancelled: acc.cancelled + (m.cancelled || 0) }),
        { completed: 0, cancelled: 0 }
      ),
    [monthlyAppointments]
  );

  // Seriler: tamamlanan tema renginde (öne çıkan), iptal soluk gri
  const series = [
    { key: "completed", label: "Tamamlanan", color: primary },
    { key: "cancelled", label: "İptal", color: COLORS.slate300 },
  ];

  const statsPending = statsLoading || !stats;
  const activityPending = activityFetching && activity.length === 0;
  const staffPending = staffFetching && staffActivity.length === 0;
  const pLabel = periodLabel(period);
  const completed = stats?.completedInPeriod;
  const cancelled = stats?.cancelledInPeriod;
  const patients = stats?.totalPatients;

  return (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", bgcolor: COLORS.slate50 }}>
      {/* ── Başlık şeridi ── */}
      <PageHeaderBar
        icon={<DashboardIcon sx={{ fontSize: 22 }} />}
        title={clinicName}
        subtitle="Genel Bakış"
        action={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              component="span"
              sx={{
                display: { xs: "none", sm: "inline-flex" },
                alignItems: "center",
                gap: 0.75,
                px: 1.25,
                py: 0.4,
                borderRadius: 999,
                fontSize: TYPO.label,
                fontWeight: 500,
                color: COLORS.successDark,
                bgcolor: alpha(COLORS.success, 0.1),
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: COLORS.success,
                  "@keyframes pulse": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.35 } },
                  animation: "pulse 2s ease-in-out infinite",
                  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
                }}
              />
              Canlı
            </Box>
            <PeriodSelector
              variant="segmented"
              options={DASHBOARD_PERIODS}
              value={period}
              onChange={setPeriod}
              palette={palette}
              isLoading={statsLoading}
            />
          </Box>
        }
      />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", ...CARD_PAGE_PAD }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: CARD_GAP, height: { lg: "100%" } }}>
          {/* ── Özet ── */}
          <Box
            sx={{
              display: "grid",
              gap: CARD_GAP,
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            }}
          >
            <KpiTile
              loading={statsPending}
              label={`Toplam ${terms.patientLower}`}
              value={nf(patients?.value)}
              hint={patients?.newThisMonth !== undefined ? `Bu ay ${nf(patients.newThisMonth)} yeni ${terms.patientLower}` : "Kliniğe kayıtlı"}
            />
            <KpiTile loading={statsPending} label="Token bakiyesi" value={nf(stats?.tokenBalance)} hint="Güncel bakiye" />
            <KpiTile
              loading={statsPending}
              label={`Tamamlanan randevu · ${pLabel}`}
              value={nf(completed?.value)}
              hint={<TrendHint change={completed?.change} direction={completed?.direction} />}
            />
            <KpiTile
              loading={statsPending}
              label={`İptal edilen · ${pLabel}`}
              value={nf(cancelled?.value)}
              hint={<TrendHint change={cancelled?.change} direction={cancelled?.direction} goodWhenUp={false} />}
            />
          </Box>

          {/* ── Randevular | Personel ── */}
          <Box sx={{ display: "grid", gap: CARD_GAP, flex: { lg: 1 }, minHeight: { lg: 240 }, gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" } }}>
            <DashCard
              icon={<EventNoteIcon sx={{ fontSize: 18 }} />}
              title="Randevular"
              subtitle={`Son ${MONTHS_IN_CHART} ayda tamamlanan ve iptal edilen randevular`}
              bodyHeight={TOP_BODY_HEIGHT}
            >
              {monthlyLoading || !monthlyData ? (
                <BarsSkeleton />
              ) : monthlyTotals.completed + monthlyTotals.cancelled === 0 ? (
                <CardEmpty
                  icon={<EventNoteIcon />}
                  title="Henüz randevu yok"
                  description="Randevular tamamlandıkça ya da iptal edildikçe aylık dağılım burada görünür."
                />
              ) : (
                <>
                  <ChartLegend series={series} totals={monthlyTotals} />
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyAppointments} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barGap={4}>
                        <CartesianGrid stroke={COLORS.slate100} vertical={false} />
                        <XAxis
                          dataKey="label"
                          tickLine={false}
                          axisLine={{ stroke: COLORS.slate200 }}
                          tick={{ fontSize: 12, fill: COLORS.slate500 }}
                        />
                        <YAxis
                          allowDecimals={false}
                          tickLine={false}
                          axisLine={false}
                          tick={{ fontSize: 12, fill: COLORS.slate500 }}
                          width={44}
                        />
                        <Tooltip content={<ChartTip series={series} />} cursor={{ fill: alpha(COLORS.slate200, 0.4) }} />
                        {series.map((s) => (
                          <Bar key={s.key} dataKey={s.key} name={s.label} fill={s.color} radius={[4, 4, 0, 0]} maxBarSize={22} />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </>
              )}
            </DashCard>

            <DashCard
              icon={<PeopleIcon sx={{ fontSize: 18 }} />}
              title="Personel"
              subtitle={`${pLabel} en çok işlem yapanlar`}
              bodyHeight={TOP_BODY_HEIGHT}
              flush
            >
              {staffPending ? (
                <RowsSkeleton avatar />
              ) : staffActivity.length === 0 ? (
                <CardEmpty
                  icon={<PeopleIcon />}
                  title="Bu dönemde personel hareketi yok"
                  description="Personel kayıt oluşturdukça ya da güncelledikçe burada sıralanır."
                />
              ) : (
                <Box sx={{ flex: 1, minHeight: 0, ...scrollSx }}>
                  {staffActivity.map((s, i) => {
                    const clickable = Boolean(s.userId);
                    return (
                      <ButtonBase
                        key={s.userId || i}
                        disabled={!clickable}
                        onClick={() => navigate(`/Personel-Yonetimi?userId=${s.userId}`)}
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          textAlign: "left",
                          px: CARD_PAD,
                          py: 1.1,
                          borderBottom: `1px solid ${COLORS.slate100}`,
                          "&:hover": { bgcolor: alpha(primary, 0.03) },
                          "&:hover .row-chevron": { opacity: 1 },
                        }}
                      >
                        <Avatar
                          sx={{ width: 32, height: 32, fontSize: 12.5, fontWeight: 600, bgcolor: alpha(primary, 0.1), color: primary }}
                        >
                          {(s.name || "?").charAt(0).toLocaleUpperCase("tr")}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography noWrap sx={{ fontSize: TYPO.body, fontWeight: 500, color: COLORS.slate800 }}>
                            {s.name}
                          </Typography>
                          <Typography noWrap sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>
                            {getRoleLabel(s.role)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography sx={{ fontSize: TYPO.body, fontWeight: 600, color: COLORS.slate800, fontVariantNumeric: "tabular-nums" }}>
                            {nf(s.actions)}
                          </Typography>
                          <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500 }}>işlem</Typography>
                        </Box>
                        {s.trend === "up" ? (
                          <TrendingUpIcon titleAccess="Önceki döneme göre arttı" sx={{ fontSize: 16, color: COLORS.successDark }} />
                        ) : (
                          <TrendingDownIcon titleAccess="Önceki döneme göre azaldı" sx={{ fontSize: 16, color: COLORS.slate400 }} />
                        )}
                        {clickable ? (
                          <ChevronRightIcon
                            className="row-chevron"
                            sx={{ fontSize: 16, color: COLORS.slate400, opacity: 0, transition: "opacity .15s" }}
                          />
                        ) : null}
                      </ButtonBase>
                    );
                  })}
                </Box>
              )}
            </DashCard>
          </Box>

          {/* ── Son işlemler ── */}
          <DashCard
            icon={<ActivityIcon sx={{ fontSize: 18 }} />}
            title="Son işlemler"
            subtitle={`${pLabel} · token harcayan klinik işlemleri`}
            bodyHeight={ACTIVITY_BODY_HEIGHT}
            flush
            sx={{ flex: { lg: 1 }, minHeight: { lg: 220 } }}
          >
            {activityPending ? (
              <RowsSkeleton />
            ) : activity.length === 0 ? (
              <CardEmpty
                icon={<ActivityIcon />}
                title="Bu dönemde işlem yok"
                description="Tedavi, reçete, randevu gibi token harcayan işlemler yapıldıkça burada listelenir."
              />
            ) : (
              <Box sx={{ flex: 1, minHeight: 0, ...scrollSx }}>
                {activity.map((a, i) => {
                  const meta = (a.type === "patient" ? { ...ACTIVITY_META.patient, label: terms.patient } : ACTIVITY_META[a.type]) || { label: "İşlem", color: COLORS.slate400 };
                  const [what, tokens] = String(a.text || "").split(" • ");
                  return (
                    <Box
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: CARD_PAD,
                        py: 1.1,
                        borderBottom: `1px solid ${COLORS.slate100}`,
                      }}
                    >
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: meta.color, flexShrink: 0 }} />
                      <Typography sx={{ width: 64, flexShrink: 0, fontSize: TYPO.caption, color: COLORS.slate500 }}>
                        {meta.label}
                      </Typography>
                      <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: TYPO.body, color: COLORS.slate800 }} title={what}>
                        {what}
                      </Typography>
                      {tokens ? (
                        <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate600, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                          {tokens}
                        </Typography>
                      ) : null}
                      <Typography sx={{ width: 84, textAlign: "right", flexShrink: 0, fontSize: TYPO.caption, color: COLORS.slate400, whiteSpace: "nowrap" }}>
                        {a.time} önce
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </DashCard>
        </Box>
      </Box>
    </Box>
  );
}
